import { rh } from '@shared-ui/theme/scale'
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AccessibilityInfo, Animated, Easing, Platform, type StyleProp, View, type ViewStyle } from 'react-native'
import { StartupMark } from './mark'
import { useCreateStyles } from './styles'

const ReadyContext = createContext(() => {})
export const useStartupReady = () => useContext(ReadyContext)
const nativeDriver = Platform.OS !== 'web'
const easeOut = Easing.bezier(0.16, 1, 0.3, 1)
// Web: HTML static-splash уже закрыл boot — длинное mark-интро только тянет паузу до home.
const isWeb = Platform.OS === 'web'

export type AppStartupTransitionProps = PropsWithChildren<{
  onVisible?: () => void
  style?: StyleProp<ViewStyle>
  testID?: string
}>

export function AppStartupTransition({ children, onVisible, style, testID }: AppStartupTransitionProps) {
  const styles = useCreateStyles()
  const [screenReady, setScreenReady] = useState(false)
  const [artworkReady, setArtworkReady] = useState(0)
  const [introComplete, setIntroComplete] = useState(false)
  const [finished, setFinished] = useState(false)
  const [reduceMotion, setReduceMotion] = useState<boolean | null>(isWeb ? false : null)
  const arrival = useRef(new Animated.Value(0)).current
  const signature = useRef(new Animated.Value(0)).current
  const sheen = useRef(new Animated.Value(0)).current
  const reveal = useRef(new Animated.Value(0)).current
  const didHideSplashRef = useRef(false)
  const markReady = useCallback(() => setScreenReady(true), [])
  const imageSettled = useCallback(() => setArtworkReady((count) => count + 1), [])

  // Уважаем системную настройку движения (на native; на web не блокируем старт).
  useEffect(() => {
    if (isWeb) return

    let active = true
    void AccessibilityInfo.isReduceMotionEnabled().then(
      (value) => {
        if (active) setReduceMotion(value)
      },
      () => {
        if (active) setReduceMotion(true)
      },
    )
    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotion)
    return () => {
      active = false
      subscription.remove()
    }
  }, [])

  // Ошибка роутера или загрузки картинки не должна навсегда закрывать приложение заставкой.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setScreenReady(true)
      setIntroComplete(true)
    }, 6000)
    return () => clearTimeout(timeout)
  }, [])

  // Знак проявляется только после декодирования картинки.
  // Web: HTML-лоадер уже был — интро знака пропускаем.
  useEffect(() => {
    if (isWeb) {
      setIntroComplete(true)
      return
    }

    if (reduceMotion === null || artworkReady < 1) return

    if (reduceMotion) {
      setIntroComplete(true)
      return
    }

    const timing = (value: Animated.Value, duration: number, delay = 0) =>
      Animated.timing(value, {
        toValue: 1,
        duration,
        delay,
        easing: easeOut,
        useNativeDriver: nativeDriver,
      })
    const animation = Animated.parallel([timing(arrival, 1000), timing(signature, 500, 450), timing(sheen, 900, 100)])
    animation.start(({ finished: completed }) => {
      if (completed) setIntroComplete(true)
    })
    return () => animation.stop()
  }, [arrival, artworkReady, reduceMotion, sheen, signature])

  // Уход знака и проявление готового экрана используют один таймлайн.
  // Static HTML splash снимаем здесь — не раньше, иначе пустая пауза mark-интро.
  useEffect(() => {
    if (!screenReady || !introComplete) return

    if (!didHideSplashRef.current) {
      didHideSplashRef.current = true
      onVisible?.()
    }

    const animation = Animated.timing(reveal, {
      toValue: 1,
      duration: reduceMotion || isWeb ? 200 : 420,
      easing: Easing.bezier(0.4, 0, 0.2, 1),
      useNativeDriver: nativeDriver,
    })
    animation.start(({ finished: completed }) => {
      if (completed) setFinished(true)
    })
    return () => animation.stop()
  }, [introComplete, onVisible, reduceMotion, reveal, screenReady])

  const contentTransform =
    finished || reduceMotion || isWeb
      ? []
      : [{ translateY: reveal.interpolate({ inputRange: [0, 1], outputRange: [rh(8), 0] }) }]

  return (
    <ReadyContext.Provider value={markReady}>
      <View style={[styles.root, style]} testID={testID}>
        <Animated.View
          style={[
            styles.content,
            {
              opacity: reveal,
              transform: contentTransform,
            },
          ]}
          pointerEvents={finished ? 'auto' : 'none'}
          accessibilityElementsHidden={!finished}
          importantForAccessibility={finished ? 'auto' : 'no-hide-descendants'}
        >
          {children}
        </Animated.View>
        {!finished && (
          <Animated.View
            style={[
              styles.overlay,
              {
                opacity: reveal.interpolate({ inputRange: [0, 0.2, 1], outputRange: [1, 1, 0] }),
              },
            ]}
            testID="startup-splash"
          >
            {/* Web: HTML static-splash остаётся до reveal — mark не дублируем. */}
            {!isWeb && (
              <StartupMark
                arrival={arrival}
                reveal={reveal}
                sheen={sheen}
                isReduceMotion={reduceMotion !== false}
                onImageSettled={imageSettled}
              />
            )}
          </Animated.View>
        )}
      </View>
    </ReadyContext.Provider>
  )
}
